import z from 'zod';
import {ObjectIdSchema} from './zod';
import {ObjectId} from 'bson';
import {IntentModel} from './customer';
import {AccountFields, isAccountField, resolveField} from './fields/keyplay';
import {EnrichedFields} from './fields/enrichment';
import {FieldDefinition} from './fields/types';

export type IntentDate = {
  customerId: ObjectId;
  accountId: ObjectId;
  date: Date;
};

const IntentSourceBaseSchema = z.object({
  id: ObjectIdSchema,
  label: z.string(),
  type: z.string(),
});
export type IntentSourceBase = z.infer<typeof IntentSourceBaseSchema>;

const ApiIntentSourceSchema = IntentSourceBaseSchema.extend({
  type: z.literal('api'),
});
export type ApiIntentSource = z.infer<typeof ApiIntentSourceSchema>;

const WebIntentSourceSchema = IntentSourceBaseSchema.extend({
  type: z.literal('webIntent'),
  path: z.string(),
});
export type WebIntentSource = z.infer<typeof WebIntentSourceSchema>;

const PhantomBusterIntentSourceSchema = IntentSourceBaseSchema.extend({
  type: z.literal('phantomBuster'),
  agentId: z.string(),
});
export type PhantomBusterIntentSource = z.infer<
  typeof PhantomBusterIntentSourceSchema
>;

const IntentSourceSchema = z.discriminatedUnion('type', [
  ApiIntentSourceSchema,
  PhantomBusterIntentSourceSchema,
  WebIntentSourceSchema,
]);
export type IntentSource = z.infer<typeof IntentSourceSchema>;

export const IntentSourceLabels = {
  api: 'API',
  phantomBuster: 'PhantomBuster',
  webIntent: 'Web Intent',
} as const satisfies Record<IntentSource['type'], string>;

export const IntentDataSchema = z.object({
  meta: z.object({
    customerId: ObjectIdSchema,
    accountId: ObjectIdSchema,
    sourceId: ObjectIdSchema,
  }),
  timestamp: z.date(),
  value: z.number(),
});
export type IntentData = z.infer<typeof IntentDataSchema>;

export const IntentDataGuard = z.object({
  customerId: ObjectIdSchema,
  accountId: ObjectIdSchema,
  sourceId: ObjectIdSchema,
  timestamp: z.date(),
});
export type IntentDataGuard = z.infer<typeof IntentDataGuard>;

/**
 * Get intent field weights and values for a specific account based on an intent model.
 * Returns a list of intent fields with their values, weights, and calculated points.
 */
export function getAccountIntentFieldWeights({
  fieldsWithWeights,
  accountFields,
  enrichedFields,
  fieldDefinitions,
}: {
  fieldsWithWeights: IntentModel['fieldsWithWeights'];
  accountFields: AccountFields;
  enrichedFields?: EnrichedFields;
  fieldDefinitions?: FieldDefinition[];
}) {
  return fieldsWithWeights
    .map(({field, weight}) => {
      const resolvedField = resolveField(field, fieldDefinitions ?? []);
      const fieldLabel = resolvedField?.label ?? 'Unknown Field';

      const value = isAccountField(field)
        ? (accountFields[field] as number)
        : (enrichedFields?.[field.toString()] as number);

      return {
        field,
        label: fieldLabel,
        points: value * weight,
        value,
        weight,
      };
    })
    .filter((field) => field.value);
}
